import { Box, ButtonBase, createStyles, makeStyles } from '@material-ui/core'
import React from 'react'
import { Colors, DynamicMuiIcon, Tooltip, Typography } from 'src/components/atoms'
import { DocumentFolder } from 'src/fixtures/types/folder'
import { FolderItem } from './FolderItem'
import { FolderLink } from './FolderLink'

const useStyles = makeStyles(() => {
  return createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    },
    folderSection: {
      margin: '12px 0',
      padding: '8px 0',
      borderTop: `1px solid ${Colors.background.gray}`,
      borderBottom: `1px solid ${Colors.background.gray}`,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      flex: 1,
      overflow: 'hidden'
    },
    folderSectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      margin: '0 8px',
      flexShrink: 0
    },
    folderSectionTitle: {
      fontSize: '12px',
      fontWeight: 600,
      color: Colors.base.middleGray,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    folderAddButton: {
      width: '24px',
      height: '24px',
      border: 'none',
      background: 'transparent',
      color: Colors.base.middleGray,
      cursor: 'pointer',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: Colors.background.gray,
        color: Colors.accent.keyPurple.default
      }
    },
    folderList: {
      marginTop: '4px',
      overflowY: 'auto',
      flex: 1,
      minHeight: 0
    }
  })
})

export interface SubSideFolderListProps {
  folderList: {
    normal: FolderData
    archive: FolderData
  }
  currentPage: string
  documentFolders?: DocumentFolder[]
  selectedFolderId?: string | null
  canManageFolders?: boolean
  showFolderSection?: boolean
  onAddFolder?: () => void
  onEditFolder?: (folder: DocumentFolder) => void
  onDeleteFolder?: (folder: DocumentFolder) => void
}

export interface FolderData {
  path: string
  count: number
}

export const SubSideFolderList = ({
  folderList,
  currentPage,
  documentFolders = [],
  selectedFolderId,
  canManageFolders = false,
  showFolderSection = true,
  onAddFolder,
  onEditFolder,
  onDeleteFolder
}: SubSideFolderListProps) => {
  const classes = useStyles()
  const { normal, archive } = folderList

  return (
    <Box className={classes.root} width={1} py="16px">
      {/* すべて */}
      <Box pb="12px" flexShrink={0}>
        <FolderLink
          path={normal.path}
          label="すべて"
          count={normal.count}
          isActive={currentPage === normal.path && !selectedFolderId}
        />
      </Box>

      {/* フォルダセクション */}
      {showFolderSection ? (
        <Box className={classes.folderSection}>
          <Box className={classes.folderSectionHeader}>
            <Typography className={classes.folderSectionTitle}>フォルダ</Typography>
            {canManageFolders && (
              <ButtonBase className={classes.folderAddButton} onClick={onAddFolder}>
                <DynamicMuiIcon variant="add" size="16px" />
              </ButtonBase>
            )}
          </Box>
          {documentFolders.length > 0 && (
            <Box className={classes.folderList}>
              {documentFolders.map(folder => (
                <FolderItem
                  key={folder.id}
                  folder={folder}
                  isActive={selectedFolderId === folder.id}
                  canManage={canManageFolders}
                  onEdit={onEditFolder}
                  onDelete={onDeleteFolder}
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box my="12px" borderTop={`1px solid ${Colors.background.gray}`} />
      )}

      {/* アーカイブ */}
      <Box pt="12px" flexShrink={0}>
        <Tooltip
          content={
            '利用が少ない資料サイトを一時保管している場所です。アーカイブにある資料サイトは、非公開の状態になります。また、アーカイブの状態から元に戻すことができます。'
          }
        >
          <Box width={1}>
            <FolderLink
              path={archive.path}
              icon="archive"
              label="アーカイブ"
              count={archive.count}
              isActive={currentPage === archive.path}
            />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  )
}
